"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Tooltip } from "react-tooltip";
import { usePrefetchWaveData } from "@/hooks/usePrefetchWaveData";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import WavePicture from "@/components/waves/WavePicture";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import type { PinnedWaveSnapshot } from "@/hooks/usePinnedWaves";
import { getWaveHomeRoute, getWaveRoute } from "@/helpers/navigation.helpers";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
interface BrainContentPinnedWaveProps {
  readonly wave: PinnedWaveSnapshot;
  readonly currentWaveId: string | null;
  readonly active: boolean;
  readonly onMouseEnter: (waveId: string) => void;
  readonly onMouseLeave: () => void;
  readonly onRemove: (waveId: string) => void;
}
const BrainContentPinnedWave: React.FC<BrainContentPinnedWaveProps> = t0 => {
  const $ = _c(60);
  const {
    wave,
    currentWaveId,
    active,
    onMouseEnter,
    onMouseLeave,
    onRemove
  } = t0;
  const router = useRouter();
  const prefetchWaveData = usePrefetchWaveData();
  const {
    registerWave
  } = useMyStream();
  const {
    isApp
  } = useDeviceInfo();
  const isMobile = useIsMobileDevice();
  let t1;
  if ($[0] !== wave.name) {
    t1 = wave.name?.trim();
    $[0] = wave.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const trimmedWaveName = t1;
  let t2;
  if ($[2] !== trimmedWaveName || $[3] !== wave.id) {
    t2 = trimmedWaveName && trimmedWaveName.length > 0 ? trimmedWaveName : wave.id.slice(0, 8);
    $[2] = trimmedWaveName;
    $[3] = wave.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const waveLabel = t2;
  const isDropWave = wave.type !== null && wave.type !== ApiWaveType.Chat;
  let t3;
  if ($[5] !== currentWaveId || $[6] !== isApp || $[7] !== wave.isDirectMessage) {
    t3 = waveId => {
      if (currentWaveId === waveId) {
        return getWaveHomeRoute({
          isDirectMessage: wave.isDirectMessage,
          isApp
        });
      }
      return getWaveRoute({
        waveId,
        isDirectMessage: wave.isDirectMessage,
        isApp
      });
    };
    $[5] = currentWaveId;
    $[6] = isApp;
    $[7] = wave.isDirectMessage;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const getHref = t3;
  let t4;
  if ($[9] !== getHref || $[10] !== onMouseLeave || $[11] !== router || $[12] !== wave.id) {
    t4 = e => {
      e.preventDefault();
      onMouseLeave();
      router.push(getHref(wave.id));
    };
    $[9] = getHref;
    $[10] = onMouseLeave;
    $[11] = router;
    $[12] = wave.id;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const onLinkClick = t4;
  let t5;
  if ($[14] !== currentWaveId || $[15] !== onMouseEnter || $[16] !== prefetchWaveData || $[17] !== registerWave || $[18] !== wave.id) {
    t5 = () => {
      onMouseEnter(wave.id);
      if (wave.id === currentWaveId) {
        return;
      }
      registerWave(wave.id);
      prefetchWaveData(wave.id);
    };
    $[14] = currentWaveId;
    $[15] = onMouseEnter;
    $[16] = prefetchWaveData;
    $[17] = registerWave;
    $[18] = wave.id;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const onHover = t5;
  let t6;
  if ($[20] !== onRemove || $[21] !== wave.id) {
    t6 = e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      onRemove(wave.id);
    };
    $[20] = onRemove;
    $[21] = wave.id;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const onRemoveClick = t6;
  const t7 = `tw-group tw-relative tw-transition-all tw-duration-300 ${!active ? "tw-opacity-70" : ""}`;
  let t8;
  if ($[23] !== getHref || $[24] !== wave.id) {
    t8 = getHref(wave.id);
    $[23] = getHref;
    $[24] = wave.id;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = `wave-tooltip-${wave.id}`;
  const t10 = `tw-relative tw-flex tw-h-6 tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-lg tw-px-1.5 tw-transition-all tw-duration-300 ${active ? "tw-bg-primary-500/15 tw-ring-1 tw-ring-inset tw-ring-primary-400/30" : "tw-bg-iron-800 tw-ring-1 tw-ring-inset tw-ring-iron-700"} `;
  let t11;
  if ($[26] !== wave.contributors || $[27] !== wave.name || $[28] !== wave.picture || $[29] !== waveLabel) {
    t11 = <div className="tw-relative tw-flex tw-size-3.5 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-iron-900">{wave.name || wave.picture || wave.contributors.length > 0 ? <WavePicture name={waveLabel} picture={wave.picture} contributors={wave.contributors} /> : <div className="tw-h-full tw-w-full tw-bg-iron-800" />}</div>;
    $[26] = wave.contributors;
    $[27] = wave.name;
    $[28] = wave.picture;
    $[29] = waveLabel;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = `tw-relative tw-whitespace-nowrap tw-text-[11px] tw-font-medium ${active ? "tw-text-primary-400" : "tw-text-iron-200"}`;
  let t13;
  if ($[31] !== t12 || $[32] !== waveLabel) {
    t13 = <span className={t12}>{waveLabel}</span>;
    $[31] = t12;
    $[32] = waveLabel;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== isDropWave) {
    t14 = isDropWave && <svg className="tw-size-3 tw-flex-shrink-0 tw-text-[#E8D48A]" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0C337 448 320 431 320 410.1c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z" /></svg>;
    $[34] = isDropWave;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <svg className="tw-size-4 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M17 7L7 17M7 7L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== onRemoveClick) {
    t16 = <button type="button" onClick={onRemoveClick} aria-label="Remove wave" className="tw-relative -tw-mr-0.5 tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0.5 tw-text-iron-400 tw-opacity-100 tw-transition-all tw-duration-300 desktop-hover:hover:tw-text-red">{t15}</button>;
    $[37] = onRemoveClick;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t13 || $[42] !== t14 || $[43] !== t16 || $[44] !== t9) {
    t17 = <div data-tooltip-id={t9} className={t10}>{t11}{t13}{t14}{t16}</div>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t13;
    $[42] = t14;
    $[43] = t16;
    $[44] = t9;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== isMobile || $[47] !== wave.id || $[48] !== waveLabel) {
    t18 = !isMobile && <Tooltip id={`wave-tooltip-${wave.id}`} place="top" style={{
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px"
    }}><span className="tw-text-xs">{waveLabel}</span></Tooltip>;
    $[46] = isMobile;
    $[47] = wave.id;
    $[48] = waveLabel;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== onLinkClick || $[51] !== t17 || $[52] !== t18 || $[53] !== t8) {
    t19 = <Link href={t8} prefetch={false} onClick={onLinkClick} className="tw-group tw-flex tw-items-center tw-no-underline">{t17}{t18}</Link>;
    $[50] = onLinkClick;
    $[51] = t17;
    $[52] = t18;
    $[53] = t8;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== onHover || $[56] !== onMouseLeave || $[57] !== t19 || $[58] !== t7) {
    t20 = <div className={t7} onMouseEnter={onHover} onMouseLeave={onMouseLeave}>{t19}</div>;
    $[55] = onHover;
    $[56] = onMouseLeave;
    $[57] = t19;
    $[58] = t7;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  return t20;
};
export default BrainContentPinnedWave;